"use client";

import { useState } from "react";
const API_HOST =
  process.env.REACT_APP_API_URL ||
  "https://crosslisting-backend-aacranbzdyd3ffbd.centralindia-01.azurewebsites.net";

// ─── CONFIG — change these to match your setup ────────────────
const BACKEND_URL = API_HOST + "/api";
const COMPANY_ID = 8;
const CONCURRENCY = 20;
const DELAY_MS = 150;

export default function FleekPricingTestPage() {
  const [products, setProducts] = useState([]);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [error, setError] = useState("");
  const [filterDecision, setFilterDecision] = useState("all");
  const [expandedRow, setExpandedRow] = useState(null);
  const [search, setSearch] = useState("");

  // ── Step 1: Load products ──
  const loadProducts = async () => {
    setError("");
    setLoading(true);
    setResults([]);
    try {
      const res = await fetch(
        `${BACKEND_URL}/fleek-pricing/bulk-source?company_id=${COMPANY_ID}`,
      );
      const json = await res.json();
      if (!json.success)
        throw new Error(json.error || "Failed to load products");

      const valid = json.data.filter((p) => p.title);
      setProducts(valid);
      setProgress({ done: 0, total: valid.length });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // ── Step 2: Call pricing API for ONE product ──
  const fetchPricing = async (product) => {
    const params = new URLSearchParams({ title: product.title });
    if (product.brand) params.append("brand", product.brand);
    if (product.size) params.append("size", product.size);
    if (product.ebay_price) params.append("market_price", product.ebay_price);

    try {
      const res = await fetch(
        `${BACKEND_URL}/fleek-pricing/suggest?${params.toString()}`,
      );
      const json = await res.json();

      if (!json.success) {
        return {
          ...product,
          status: "ERROR",
          error: json.error || "Unknown error",
        };
      }

      return {
        ...product,
        status: "OK",
        decision: json.decision,
        confidence: json.confidence,
        match_level: json.match_level,
        detected_category: json.detected_category,
        matched_products: json.matched_products,
        outliers_removed: json.outliers_removed,
        cost_floor: json.pricing?.cost_floor,
        market_price: json.pricing?.market_price,
        gross_profit: json.pricing?.gross_profit,
        TO_margin: json.pricing?.TO_margin,
        reseller_cut: json.pricing?.reseller_cut,
        list_price: json.pricing?.list_price,
        shortfall: json.pricing?.shortfall,
        shipping_source: json.shipping_source,
        market_price_source: json.market_price_source,
        currency: json.currency,
        warning: json.warning,
        comparison: json.comparison,
        formula_steps: json.formula_steps,
        cost_breakdown: json.cost_breakdown,
        sample_matches: json.sample_matches,
      };
    } catch (err) {
      return { ...product, status: "ERROR", error: err.message };
    }
  };

  // ── Step 3: Run bulk ──
  const runBulkTest = async () => {
    if (!products.length) {
      setError("Load products first");
      return;
    }
    setLoading(true);
    setError("");
    setResults([]);
    setProgress({ done: 0, total: products.length });

    const allResults = [];
    for (let i = 0; i < products.length; i += CONCURRENCY) {
      const batch = products.slice(i, i + CONCURRENCY);
      const batchResults = await Promise.all(batch.map(fetchPricing));
      allResults.push(...batchResults);
      setResults([...allResults]);
      setProgress({ done: allResults.length, total: products.length });
      if (i + CONCURRENCY < products.length) {
        await new Promise((r) => setTimeout(r, DELAY_MS));
      }
    }
    setLoading(false);
  };

  // ── Stats ──
  const listCount = results.filter((r) => r.decision === "LIST").length;
  const rejectCount = results.filter((r) => r.decision === "REJECT").length;
  const errorCount = results.filter((r) => r.status === "ERROR").length;

  const totalGrossProfit = results
    .filter((r) => r.decision === "LIST")
    .reduce((sum, r) => sum + (r.gross_profit || 0), 0);
  const totalTOMargin = results
    .filter((r) => r.decision === "LIST")
    .reduce((sum, r) => sum + (r.TO_margin || 0), 0);
  const totalResellerCut = results
    .filter((r) => r.decision === "LIST")
    .reduce((sum, r) => sum + (r.reseller_cut || 0), 0);

  const filteredResults = results
    .filter((r) => {
      if (filterDecision === "all") return true;
      if (filterDecision === "ERROR") return r.status === "ERROR";
      return r.decision === filterDecision;
    })
    .filter((r) =>
      search ? r.title?.toLowerCase().includes(search.toLowerCase()) : true,
    );

  // ── CSV Export ──
  const exportCSV = () => {
    const headers = [
      "Product ID",
      "SKU",
      "Title",
      "Brand",
      "Size",
      "Detected Category",
      "Decision",
      "Confidence",
      "Match Level",
      "Matched Products",
      "Outliers Removed",
      "Market Price",
      "Market Price Source",
      "Cost Floor",
      "Shipping Source",
      "Gross Profit",
      "TO Margin",
      "Reseller Cut",
      "List Price",
      "Shortfall",
      "Median Item Price",
      "Multiplied (x2.25)",
      "Median Shipping",
      "Currency",
      "Warning",
      "Error",
    ];

    const rows = filteredResults.map((r) => [
      r.product_id,
      r.sku,
      csvEscape(r.title),
      r.brand,
      r.size,
      r.detected_category,
      r.decision || r.status,
      r.confidence,
      r.match_level,
      r.matched_products,
      r.outliers_removed,
      r.market_price,
      r.market_price_source,
      r.cost_floor,
      r.shipping_source,
      r.gross_profit,
      r.TO_margin,
      r.reseller_cut,
      r.list_price,
      r.shortfall,
      r.cost_breakdown?.median_item_price,
      r.cost_breakdown?.multiplied,
      r.cost_breakdown?.median_shipping,
      r.currency,
      csvEscape(r.warning),
      csvEscape(r.error),
    ]);

    const csvContent = [headers, ...rows]
      .map((row) =>
        row
          .map((cell) => (cell === undefined || cell === null ? "" : cell))
          .join(","),
      )
      .join("\n");

    downloadFile(csvContent, "fleek_pricing_results.csv", "text/csv");
  };

  // ── Excel Export (using simple HTML table -> .xls trick, no extra library needed) ──
  const exportExcel = () => {
    const headers = [
      "Product ID",
      "SKU",
      "Title",
      "Brand",
      "Size",
      "Detected Category",
      "Decision",
      "Confidence",
      "Match Level",
      "Matched Products",
      "Outliers Removed",
      "Market Price",
      "Market Price Source",
      "Cost Floor",
      "Shipping Source",
      "Gross Profit",
      "TO Margin",
      "Reseller Cut",
      "List Price",
      "Shortfall",
      "Median Item Price",
      "Multiplied (x2.25)",
      "Median Shipping",
      "Currency",
      "Warning",
      "Error",
    ];

    const rowsHtml = filteredResults
      .map((r) => {
        const cells = [
          r.product_id,
          r.sku,
          r.title,
          r.brand,
          r.size,
          r.detected_category,
          r.decision || r.status,
          r.confidence,
          r.match_level,
          r.matched_products,
          r.outliers_removed,
          r.market_price,
          r.market_price_source,
          r.cost_floor,
          r.shipping_source,
          r.gross_profit,
          r.TO_margin,
          r.reseller_cut,
          r.list_price,
          r.shortfall,
          r.cost_breakdown?.median_item_price,
          r.cost_breakdown?.multiplied,
          r.cost_breakdown?.median_shipping,
          r.currency,
          r.warning,
          r.error,
        ];
        return `<tr>${cells.map((c) => `<td>${c ?? ""}</td>`).join("")}</tr>`;
      })
      .join("");

    const tableHtml = `
      <table>
        <thead><tr>${headers.map((h) => `<th>${h}</th>`).join("")}</tr></thead>
        <tbody>${rowsHtml}</tbody>
      </table>
    `;

    const excelHtml = `
      <html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns="http://www.w3.org/TR/REC-html40">
      <head><meta charset="utf-8"></head>
      <body>${tableHtml}</body>
      </html>
    `;

    downloadFile(
      excelHtml,
      "fleek_pricing_results.xls",
      "application/vnd.ms-excel",
    );
  };

  const csvEscape = (val) => {
    if (val === undefined || val === null) return "";
    const str = String(val);
    if (str.includes(",") || str.includes('"') || str.includes("\n")) {
      return `"${str.replace(/"/g, '""')}"`;
    }
    return str;
  };

  const downloadFile = (content, filename, mime) => {
    const blob = new Blob([content], { type: `${mime};charset=utf-8;` });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div
      style={{
        padding: "24px",
        fontFamily: "system-ui, sans-serif",
        maxWidth: "1800px",
        margin: "0 auto",
      }}
    >
      <h1 style={{ fontSize: "22px", fontWeight: 600, marginBottom: "8px" }}>
        Fleek Pricing — Bulk Test
      </h1>
      <p style={{ color: "#666", marginBottom: "20px", fontSize: "14px" }}>
        Loads products from company_id={COMPANY_ID} and tests each against
        /fleek-pricing/suggest
      </p>

      {/* ── Controls ── */}
      <div
        style={{
          display: "flex",
          gap: "12px",
          marginBottom: "20px",
          alignItems: "center",
          flexWrap: "wrap",
        }}
      >
        <button
          onClick={loadProducts}
          disabled={loading}
          style={btnStyle("#2563eb")}
        >
          1. Load Products
        </button>

        <button
          onClick={runBulkTest}
          disabled={loading || !products.length}
          style={btnStyle(products.length ? "#16a34a" : "#9ca3af")}
        >
          2. Run Bulk Test ({products.length} products)
        </button>

        {results.length > 0 && (
          <>
            <select
              value={filterDecision}
              onChange={(e) => setFilterDecision(e.target.value)}
              style={selectStyle}
            >
              <option value="all">All ({results.length})</option>
              <option value="LIST">✅ LIST ({listCount})</option>
              <option value="REJECT">❌ REJECT ({rejectCount})</option>
              <option value="ERROR">⚠️ ERROR ({errorCount})</option>
            </select>

            <input
              type="text"
              placeholder="Search title..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{
                padding: "8px 12px",
                borderRadius: "6px",
                border: "1px solid #d1d5db",
                minWidth: "200px",
              }}
            />

            <button onClick={exportCSV} style={btnStyle("#7c3aed")}>
              📄 Export CSV
            </button>
            <button onClick={exportExcel} style={btnStyle("#0d9488")}>
              📊 Export Excel
            </button>
          </>
        )}
      </div>

      {/* ── Progress ── */}
      {loading && progress.total > 0 && (
        <div style={{ marginBottom: "16px" }}>
          <div style={{ fontSize: "13px", color: "#666", marginBottom: "4px" }}>
            Processing: {progress.done} / {progress.total}
          </div>
          <div
            style={{
              width: "100%",
              height: "8px",
              background: "#e5e7eb",
              borderRadius: "4px",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                width: `${(progress.done / progress.total) * 100}%`,
                height: "100%",
                background: "#2563eb",
                transition: "width 0.2s",
              }}
            />
          </div>
        </div>
      )}

      {error && (
        <div
          style={{
            background: "#fee2e2",
            color: "#991b1b",
            padding: "12px",
            borderRadius: "6px",
            marginBottom: "16px",
          }}
        >
          {error}
        </div>
      )}

      {/* ── Summary cards ── */}
      {results.length > 0 && (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(7, 1fr)",
            gap: "12px",
            marginBottom: "20px",
          }}
        >
          <StatCard
            label="Total tested"
            value={results.length}
            color="#374151"
          />
          <StatCard label="✅ List" value={listCount} color="#16a34a" />
          <StatCard label="❌ Reject" value={rejectCount} color="#dc2626" />
          <StatCard label="⚠️ Errors" value={errorCount} color="#d97706" />
          <StatCard
            label="Total Gross Profit"
            value={`£${totalGrossProfit.toFixed(2)}`}
            color="#2563eb"
          />
          <StatCard
            label="Total TO Margin"
            value={`£${totalTOMargin.toFixed(2)}`}
            color="#7c3aed"
          />
          <StatCard
            label="Total Reseller Cut"
            value={`£${totalResellerCut.toFixed(2)}`}
            color="#0d9488"
          />
        </div>
      )}

      {/* ── Results table ── */}
      {filteredResults.length > 0 && (
        <div
          style={{
            overflowX: "auto",
            border: "1px solid #e5e7eb",
            borderRadius: "8px",
          }}
        >
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              fontSize: "13px",
            }}
          >
            <thead>
              <tr
                style={{
                  background: "#f9fafb",
                  borderBottom: "1px solid #e5e7eb",
                }}
              >
                <Th></Th>
                <Th>Title</Th>
                <Th>Brand</Th>
                <Th>Category</Th>
                <Th>Decision</Th>
                <Th>Confidence</Th>
                <Th>Match Level</Th>
                <Th>Market Price</Th>
                <Th>Cost Floor</Th>
                <Th>Gross Profit</Th>
                <Th>TO Margin</Th>
                <Th>Reseller Cut</Th>
                <Th>Shipping Src</Th>
                <Th>Matches</Th>
              </tr>
            </thead>
            <tbody>
              {filteredResults.map((r, i) => (
                <>
                  <tr
                    key={i}
                    onClick={() => setExpandedRow(expandedRow === i ? null : i)}
                    style={{
                      borderBottom: "1px solid #f3f4f6",
                      cursor: "pointer",
                    }}
                  >
                    <Td
                      style={{
                        width: "24px",
                        textAlign: "center",
                        color: "#9ca3af",
                      }}
                    >
                      {expandedRow === i ? "▼" : "▶"}
                    </Td>
                    <Td style={{ maxWidth: "260px" }}>
                      <div style={{ fontWeight: 500 }}>{r.title}</div>
                      {r.sku && (
                        <div style={{ color: "#9ca3af", fontSize: "11px" }}>
                          {r.sku}
                        </div>
                      )}
                    </Td>
                    <Td>{r.brand || "—"}</Td>
                    <Td>{r.detected_category || "—"}</Td>
                    <Td>
                      {r.status === "ERROR" ? (
                        <Badge color="#d97706" bg="#fef3c7">
                          ERROR
                        </Badge>
                      ) : r.decision === "LIST" ? (
                        <Badge color="#16a34a" bg="#dcfce7">
                          LIST
                        </Badge>
                      ) : (
                        <Badge color="#dc2626" bg="#fee2e2">
                          REJECT
                        </Badge>
                      )}
                    </Td>
                    <Td>{r.confidence || "—"}</Td>
                    <Td style={{ fontSize: "11px", color: "#6b7280" }}>
                      {r.match_level || "—"}
                    </Td>
                    <Td>
                      {r.market_price != null
                        ? `${r.market_price} ${r.currency || ""}`
                        : "—"}
                    </Td>
                    <Td>{r.cost_floor != null ? r.cost_floor : "—"}</Td>
                    <Td
                      style={{
                        color: r.gross_profit > 0 ? "#16a34a" : "#dc2626",
                      }}
                    >
                      {r.gross_profit != null
                        ? r.gross_profit
                        : r.shortfall != null
                          ? `-${r.shortfall}`
                          : "—"}
                    </Td>
                    <Td>{r.TO_margin != null ? r.TO_margin : "—"}</Td>
                    <Td>{r.reseller_cut != null ? r.reseller_cut : "—"}</Td>
                    <Td style={{ fontSize: "11px" }}>
                      {r.shipping_source || "—"}
                    </Td>
                    <Td>{r.matched_products || "—"}</Td>
                  </tr>

                  {/* ── Expanded detail row ── */}
                  {expandedRow === i && (
                    <tr>
                      <td
                        colSpan={14}
                        style={{ background: "#f9fafb", padding: "16px 20px" }}
                      >
                        <DetailPanel result={r} />
                      </td>
                    </tr>
                  )}
                </>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!loading && !results.length && !products.length && (
        <div style={{ textAlign: "center", padding: "60px", color: "#9ca3af" }}>
          Click "Load Products" to get started
        </div>
      )}
    </div>
  );
}

// ─── Detail panel (expanded row) ──────────────────────────────
function DetailPanel({ result: r }) {
  if (r.status === "ERROR") {
    return <div style={{ color: "#dc2626" }}>Error: {r.error}</div>;
  }

  return (
    <div
      style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "24px" }}
    >
      {/* ── Formula steps ── */}
      <div>
        <h4
          style={{
            fontSize: "13px",
            fontWeight: 600,
            marginBottom: "8px",
            color: "#374151",
          }}
        >
          Formula Breakdown
        </h4>
        <div
          style={{
            fontFamily: "monospace",
            fontSize: "12px",
            background: "#fff",
            padding: "12px",
            borderRadius: "6px",
            border: "1px solid #e5e7eb",
          }}
        >
          {r.formula_steps &&
            Object.entries(r.formula_steps).map(([key, val]) => (
              <div key={key} style={{ marginBottom: "4px", color: "#374151" }}>
                {val}
              </div>
            ))}
        </div>
        {r.comparison && (
          <div
            style={{
              marginTop: "8px",
              fontSize: "13px",
              fontWeight: 600,
              color: r.decision === "LIST" ? "#16a34a" : "#dc2626",
            }}
          >
            {r.comparison}
          </div>
        )}
        {r.warning && (
          <div
            style={{
              marginTop: "8px",
              fontSize: "12px",
              color: "#d97706",
              background: "#fef3c7",
              padding: "8px",
              borderRadius: "4px",
            }}
          >
            ⚠️ {r.warning}
          </div>
        )}
      </div>

      {/* ── Sample matches ── */}
      <div>
        <h4
          style={{
            fontSize: "13px",
            fontWeight: 600,
            marginBottom: "8px",
            color: "#374151",
          }}
        >
          Sample Matched Products ({r.matched_products} total,{" "}
          {r.outliers_removed} outliers removed)
        </h4>
        <div
          style={{
            background: "#fff",
            borderRadius: "6px",
            border: "1px solid #e5e7eb",
            maxHeight: "200px",
            overflowY: "auto",
          }}
        >
          <table
            style={{
              width: "100%",
              fontSize: "11px",
              borderCollapse: "collapse",
            }}
          >
            <thead>
              <tr style={{ background: "#f3f4f6" }}>
                <th style={{ padding: "6px 8px", textAlign: "left" }}>Title</th>
                <th style={{ padding: "6px 8px", textAlign: "left" }}>Grade</th>
                <th style={{ padding: "6px 8px", textAlign: "right" }}>
                  Item £
                </th>
                <th style={{ padding: "6px 8px", textAlign: "right" }}>
                  List £
                </th>
              </tr>
            </thead>
            <tbody>
              {(r.sample_matches || []).map((m, idx) => (
                <tr key={idx} style={{ borderTop: "1px solid #f3f4f6" }}>
                  <td
                    style={{
                      padding: "6px 8px",
                      maxWidth: "180px",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {m.title}
                  </td>
                  <td style={{ padding: "6px 8px" }}>{m.grade || "—"}</td>
                  <td style={{ padding: "6px 8px", textAlign: "right" }}>
                    {m.item_price}
                  </td>
                  <td style={{ padding: "6px 8px", textAlign: "right" }}>
                    {m.list_price ?? "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ─── Small UI helpers ───────────────────────────────────────
function btnStyle(bg) {
  return {
    padding: "10px 18px",
    background: bg,
    color: "#fff",
    border: "none",
    borderRadius: "6px",
    fontSize: "14px",
    fontWeight: 500,
    cursor: "pointer",
  };
}

const selectStyle = {
  padding: "8px 12px",
  borderRadius: "6px",
  border: "1px solid #d1d5db",
};

function StatCard({ label, value, color }) {
  return (
    <div
      style={{
        background: "#fff",
        border: "1px solid #e5e7eb",
        borderRadius: "8px",
        padding: "16px",
      }}
    >
      <div style={{ fontSize: "12px", color: "#6b7280", marginBottom: "4px" }}>
        {label}
      </div>
      <div style={{ fontSize: "22px", fontWeight: 700, color }}>{value}</div>
    </div>
  );
}

function Th({ children }) {
  return (
    <th
      style={{
        padding: "10px 12px",
        textAlign: "left",
        fontWeight: 600,
        color: "#374151",
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </th>
  );
}

function Td({ children, style }) {
  return (
    <td style={{ padding: "10px 12px", verticalAlign: "top", ...style }}>
      {children}
    </td>
  );
}

function Badge({ children, color, bg }) {
  return (
    <span
      style={{
        display: "inline-block",
        padding: "2px 8px",
        borderRadius: "4px",
        fontSize: "11px",
        fontWeight: 600,
        color,
        background: bg,
      }}
    >
      {children}
    </span>
  );
}
